import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

import { usePageDetails } from "../../hooks/usePageDetails";
import CreateHeading from "../../components/CreateHeading/CreateHeading";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import FormInput from "../../components/FormInput/FormInput";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import FormActionButtons from "../../components/FormActionButtons/FormActionButtons";
import {
  useCreateBrandMutation,
  useEditBrandMutation,
  useLazyGetSingleBrandDetailsQuery,
} from "../../app/api/brandApi";
import { handleApiResponse } from "../../utils/handleApiResponse";

export interface InitialFormValues {
  brand_name: string;
  is_active: boolean;
}

const CreateBrand: React.FC = () => {
  const { id, pageType } = usePageDetails();
  const [createBrandMutation] = useCreateBrandMutation();
  const [getSingleBrandDetailsQuery, getSingleBrandDetailsResponse] =
    useLazyGetSingleBrandDetailsQuery();
  const [editBrandMutation] = useEditBrandMutation();
  const navigate = useNavigate();

  const initialValues: InitialFormValues = {
    brand_name: getSingleBrandDetailsResponse?.data?.data?.brand_name ?? "",
    is_active: getSingleBrandDetailsResponse?.data?.data?.is_active ?? true,
  };

  const validationSchema = Yup.object().shape({
    brand_name: Yup.string()
      .required("Brand name is required")
      .min(2, "Brand name must be at least 2 character")
      .max(255, "Brand name must be at most 255 characters"),
    is_active: Yup.boolean(),
  });

  const getSingleBrandDetails = async () => {
    try {
      await getSingleBrandDetailsQuery({
        brandId: id,
      }).unwrap();
    } catch (error: any) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Get Brand Details Error",
      });
    }
  };

  useEffect(() => {
    if (pageType !== "create") {
      getSingleBrandDetails();
    }
    // eslint-disable-next-line
  }, [id]);

  const handleSubmit = async (
    values: InitialFormValues,
    { setFieldError }: FormikHelpers<InitialFormValues>
  ) => {
    const { brand_name, is_active } = values;
    const body = {
      brand_name,
      ...(pageType === "edit" ? { is_active } : {}),
    };

    const successMessage =
      pageType === "create"
        ? "Brand created successfully."
        : "Brand updated successfully.";

    try {
      const response =
        pageType === "create"
          ? await createBrandMutation(body).unwrap()
          : await editBrandMutation({
              brandId: id,
              body,
            }).unwrap();
      if (response?.status_code === 200 || response?.status_code === 201) {
        handleApiResponse({
          type: "success",
          message: successMessage,
          toastId: "Create Brand",
        });
        navigate("/provisioning/brand");
      }
    } catch (error: any) {
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Create Brand Error",
        setFieldError,
      });

      console.log(error);
    }
  };

  return (
    <Layout>
      <CreateHeading label={"Brand"} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          dirty,
        }) => {
          const disable = pageType === "create" ? !values.brand_name : !dirty;

          return (
            <>
              <Form className="w-100 mb-5 pb-5">
                <Col sm={6}>
                  {pageType === "view" && (
                    <Row className="my-2">
                      <FormInput
                        label="Brand ID"
                        type="text"
                        name="id"
                        placeholder="Enter account name"
                        labelClassName="m-0 fw-500"
                        controlClassName="bg-white fs-15-point-5 h-48"
                        value={getSingleBrandDetailsResponse?.data?.data?.id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required={false}
                        isValid={touched.brand_name && !errors.brand_name}
                        isInvalid={touched.brand_name && !!errors.brand_name}
                        touched={touched.brand_name}
                        error={errors.brand_name}
                        disabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  <Row className="my-2">
                    <FormInput
                      label="Brand Name"
                      type="text"
                      name="brand_name"
                      placeholder="Enter brand name"
                      labelClassName="m-0 fw-500"
                      controlClassName="bg-white fs-15-point-5 h-48"
                      value={values.brand_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required={true}
                      isValid={touched.brand_name && !errors.brand_name}
                      isInvalid={touched.brand_name && !!errors.brand_name}
                      touched={touched.brand_name}
                      error={errors.brand_name}
                      disabled={pageType === "view"}
                    />
                  </Row>
                  {pageType !== "create" && (
                    <Row className="ms-0 mt-3">
                      <ToggleSwitch
                        activeStatusText="Active"
                        inActiveStatusText="Inactive"
                        value={values.is_active}
                        onChange={(val: boolean) => {
                          setFieldValue("is_active", val);
                        }}
                        key={String(values.is_active)}
                        isDisabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  <FormActionButtons
                    disable={disable}
                    isSubmitting={isSubmitting}
                    navigateUrl="/provisioning/brand"
                  />
                </Col>
              </Form>
            </>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default CreateBrand;
